import React from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const shipping = subtotal >= 75 || subtotal === 0 ? 0 : 4.99;
  const total = subtotal + shipping;

  if (items.length === 0) {
    return (
      <div className="bg-ink min-h-[70vh] flex flex-col items-center justify-center px-5 text-center">
        <ShoppingBag size={48} className="text-[color:var(--chrome-3)] mb-6" />
        <h1 className="h-display text-3xl text-chrome">YOUR CART IS EMPTY</h1>
        <p className="text-[color:var(--chrome-3)] mt-3">Find your next great card.</p>
        <Link to="/shop" data-testid="cart-empty-shop" className="btn-holo rounded-xl px-7 py-4 text-sm mt-8">SHOP CARDS</Link>
      </div>
    );
  }

  return (
    <div className="bg-ink min-h-screen">
      <div className="mx-auto max-w-[1100px] px-5 py-12">
        <h1 className="h-display text-4xl text-chrome mb-10">YOUR CART</h1>
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {items.map((it) => (
              <div key={it.product_id} data-testid={`cart-item-${it.sku}`} className="flex gap-4 rounded-2xl border border-[color:var(--line)] p-4 bg-[color:var(--charcoal)]">
                <Link to={`/product/${it.slug}`} className="h-24 w-20 rounded-lg overflow-hidden bg-[color:var(--ink-2)] shrink-0">
                  {it.image && <img src={it.image} alt={it.title} className="h-full w-full object-cover" />}
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${it.slug}`} className="text-white font-semibold hover:text-[color:var(--cyan)] line-clamp-2">{it.title}</Link>
                  <p className="text-xs text-[color:var(--chrome-3)] mt-1">{it.sku}</p>
                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex items-center border border-[color:var(--line)] rounded-lg">
                      <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="p-2 text-white hover:text-[color:var(--cyan)]"><Minus size={14} /></button>
                      <span className="w-8 text-center text-sm text-white">{it.quantity}</span>
                      <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="p-2 text-white hover:text-[color:var(--cyan)]"><Plus size={14} /></button>
                    </div>
                    <button onClick={() => removeItem(it.product_id)} data-testid={`cart-remove-${it.sku}`} className="text-[color:var(--chrome-3)] hover:text-[color:var(--magenta)]"><Trash2 size={16} /></button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white font-display font-bold">${(it.price * it.quantity).toFixed(2)}</p>
                  <p className="text-xs text-[color:var(--chrome-3)] mt-1">${it.price.toFixed(2)} ea</p>
                </div>
              </div>
            ))}
          </div>

          <div className="glass rounded-3xl p-7 h-fit lg:sticky lg:top-24">
            <p className="eyebrow mb-6">Order Summary</p>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between text-[color:var(--chrome-2)]"><span>Subtotal</span><span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-[color:var(--chrome-2)]"><span>Shipping</span><span>{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span></div>
              {shipping > 0 && <p className="text-[11px] text-[color:var(--chrome-3)]">Free shipping over $75</p>}
              <div className="border-t border-[color:var(--line)] pt-3 flex justify-between text-white font-display font-bold text-lg"><span>Total</span><span data-testid="cart-total">${total.toFixed(2)}</span></div>
            </div>
            <Link to="/checkout" data-testid="cart-checkout" className="btn-holo w-full text-center rounded-xl py-4 text-sm mt-6 inline-flex items-center justify-center gap-2">CHECKOUT <ArrowRight size={16} /></Link>
            <Link to="/shop" className="block text-center text-xs font-tech tracking-wide text-[color:var(--chrome-3)] hover:text-white mt-4">CONTINUE SHOPPING</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
